// src/publish/pieces/pinterestPost.js — pieces of the Pinterest pin (format key 'pinterest-post').
//
// The pin has its own fields: pin_title, pin_description, board_suggestion,
// hashtags, cta_url (title / hook / body / cta arrive EMPTY; full_text is the
// backend's own composition with a «Board: …» line and is never used here).
// Pieces, each its own box in the pin composer:
//   description  primary, kind 'plain', limit LIMITS.pinterestDescription (500)
//                — pin_description (falls back to body for an older run)
//   title        kind 'plain', limit LIMITS.pinterestTitle (100), label
//                «Titolo pin» — pin_title (falls back to title)
//   board        kind 'plain', the suggested board (board_suggestion)
//   hashtags     kind 'tags', only when the backend sent any
//   link         kind 'url', label «URL di destinazione»: the brief's cta_url
//                (content.cta_url else params.cta_url), only when there is one
// A missing field gives no piece; the primary moves to the first piece when
// there is no description, so exactly one piece is primary.
// Labels / destinations: outputs:pieces.labels.pinterest-post.* and
// outputs:pieces.destinations.pinterest-post.* (see ../piece.js header).

import { normalizeText, inlineRuns } from '../normalize.js';
import { paragraph, compactBlocks } from '../blocks.js';
import { makePiece, ctaUrlOf, tagsBlocks, urlBlocks, ensureOnePrimary, str, LIMITS } from '../piece.js';

const FORMAT = 'pinterest-post';

const first = (...values) => values.map(str).find((v) => v.trim()) || '';

/** A single-line field → one paragraph (markup read, never split into paragraphs). */
const lineBlocks = (s) => compactBlocks([paragraph(inlineRuns(str(s).trim()))]);

export function build(entry) {
  const content = entry?.results?.content;
  if (!content || typeof content !== 'object') return [];
  const pieces = [];

  const description = normalizeText(first(content.pin_description, content.body));
  if (description.length) {
    pieces.push(makePiece(FORMAT, { id: 'description', primary: true, blocks: description, limit: LIMITS.pinterestDescription }));
  }

  const title = lineBlocks(first(content.pin_title, content.title));
  if (title.length) pieces.push(makePiece(FORMAT, { id: 'title', blocks: title, limit: LIMITS.pinterestTitle }));

  const board = lineBlocks(content.board_suggestion);
  if (board.length) pieces.push(makePiece(FORMAT, { id: 'board', blocks: board }));

  const tags = tagsBlocks(content.hashtags);
  if (tags.length) pieces.push(makePiece(FORMAT, { id: 'hashtags', kind: 'tags', blocks: tags }));

  const url = urlBlocks(ctaUrlOf(entry));
  if (url.length) pieces.push(makePiece(FORMAT, { id: 'link', kind: 'url', blocks: url }));

  return ensureOnePrimary(pieces);
}
